// src/pages/AdminNetworkViz.js
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { fetchWithAuth } from '../utils/fetchWithAuth'
import { modalPrimaryButtonStyle } from '../styles/buttonStyles'
import resetIcon from '../assets/images/icons/reset.png'

export default function AdminNetworkViz() {
  const { t } = useTranslation()
  const [graph, setGraph] = useState({ nodes: [], links: [] })
  const [statuses, setStatuses] = useState({})
  const [hoveredNode, setHoveredNode] = useState(null)
  const [hoveredUser, setHoveredUser] = useState(null)

  const [showPopup, setShowPopup] = useState(false)
  const [popupUser, setPopupUser] = useState(null)

  const [refBonusByChild, setRefBonusByChild] = useState({})

  const [pressedBtn, setPressedBtn] = useState(null)
  const [hoverBtn, setHoverBtn] = useState(null)
  const [zoom, setZoom] = useState(1)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })

  const ymNow = () => {
    const d = new Date()
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    return `${y}-${m}`
  }

  // Načítanie grafu
  useEffect(() => {
    fetchWithAuth('/admin/network-graph')
      .then(async res => {
        if (res.ok) {
          const data = await res.json()
          setGraph(data)
        } else {
          console.error('Chyba načítania siete', res.status)
        }
      })
      .catch(console.error)
  }, [])

  const getUserFuelBucketStrict = (user) => {
    const mt = user?.membership?.membershipType
    if (!mt) return null
    if (typeof mt.is_electric === 'boolean') {
      return mt.is_electric ? 'electric' : 'fuel'
    }
    const t = (mt.type || '').toString().toLowerCase()
    if (t.includes('elektr') || t.includes('electric') || t.includes('ev')) return 'electric'
    return 'fuel'
  }

  const getReceiptFuelBucket = (r) => {
    const raw = r?.fuel_type ?? r?.fuelType ?? r?.category ?? r?.productType ?? r?.fuel ?? r?.fuel_station_type ?? r?.fuelStationType
    if (raw) {
      const v = (typeof raw === 'string' ? raw : raw.name || '').toLowerCase()
      if (v === 'electric' || v === 'ev' || v.includes('elektro') || v.includes('kwh')) return 'electric'
      if (v === 'fuel') return 'fuel'
    }
    return 'fuel'
  }

  const getReceiptDate = (r) => {
    const raw =
      r.date ?? r.created_at ?? r.createdAt ?? r.timestamp ?? r.datetime ??
      r.purchased_at ?? r.purchasedAt ?? r.datum ?? null
    if (!raw) return null
    if (typeof raw === 'number') {
      const ms = raw < 1e12 ? raw * 1000 : raw
      const d = new Date(ms)
      return Number.isNaN(d.getTime()) ? null : d
    }
    if (typeof raw === 'string') {
      const ts = Date.parse(raw)
      if (!Number.isNaN(ts)) return new Date(ts)
      const m = raw.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})/)
      if (m) {
        const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]))
        return Number.isNaN(d.getTime()) ? null : d
      }
    }
    return null
  }

  const isInCurrentMonth = (d) => {
    if (!(d instanceof Date)) return false
    const now = new Date()
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()
  }

  // Farby uzlov: green = aktivny && má aspoň 1 processed bloček v mesiaci s bucketom členstva
  //              gray  = aktivny && NEMÁ taký processed bloček
  //              red   = neaktívny
  useEffect(() => {
    if (!graph.nodes.length) return

    ;(async () => {
      try {
        const usersRes = await fetchWithAuth('/admin/users')

        if (!usersRes.ok) {
          throw new Error('Chyba načítania používateľov')
        }

        const users = await usersRes.json()
        const usersById = Object.fromEntries(
          users.map(u => [String(u.id), u])
        )

        const receiptBuckets = graph.receiptBuckets || {}
        const map = {}

        graph.nodes.forEach(n => {
          const user = usersById[String(n.id)] || null
          const active = !!user?.aktivny

          if (!active) {
            map[n.id] = 'red'
            return
          }

          const bucket = getUserFuelBucketStrict(user)

          if (!bucket) {
            map[n.id] = 'gray'
            return
          }

          const hasFuelStrict =
            receiptBuckets[n.id]?.[bucket] === true

          map[n.id] = hasFuelStrict ? 'green' : 'gray'
        })

        setStatuses(map)
      } catch (e) {
        console.error('Chyba pri výpočte farieb', e)
      }
    })()
  }, [graph])

  const parseReferralSourceChildId = (sourceId) => {
    if (!sourceId) return null
    if (typeof sourceId === 'number') return sourceId

    const s = String(sourceId)
    const parts = s.split(':')
    const id = Number(parts[0])
    return Number.isFinite(id) ? id : null
  }

  // === Referral bonus výpočty ===
  useEffect(() => {
    setRefBonusByChild(graph.refBonusByChild || {})
  }, [graph])

  const { nodes, links } = graph
  if (!nodes.length || !links.length) return null

  const childrenMap = {}, parentMap = {}
  links.forEach(({ source, target }) => {
    if (!childrenMap[source]) childrenMap[source] = []
    childrenMap[source].push(target)
    parentMap[target] = source
  })

  const roots = nodes.filter(n => !parentMap[n.id]).map(n => n.id)

  const levelGap = 80
  const radius = 16
  const spacingX = 40
  const offset = radius + 50

  let nextX = 0
  const coords = {}

  function dfs(id, depth) {
    const ch = childrenMap[id] || []
    if (ch.length === 0) {
      coords[id] = { x: nextX * spacingX + offset, y: depth * levelGap + offset }
      nextX++
    } else {
      ch.forEach(c => dfs(c, depth + 1))
      const xs = ch.map(c => coords[c].x)
      coords[id] = {
        x: (Math.min(...xs) + Math.max(...xs)) / 2,
        y: depth * levelGap + offset
      }
    }
  }

  roots.forEach(r => dfs(r, 0))

  const allXs = Object.values(coords).map(c => c.x)
  const allYs = Object.values(coords).map(c => c.y)
  const svgWidth = Math.max(...allXs) + offset
  const svgHeight = Math.max(...allYs) + offset

  const loadPopupData = async id => {
    setShowPopup(true)
    setPopupUser(null)
    try {
      const res = await fetchWithAuth('/admin/users')
      if (!res.ok) {
        setPopupUser({ error: true })
        return
      }
      const data = await res.json()
      const user = data.find(u => String(u.id) === String(id))
      setPopupUser(user || { error: true })
    } catch {
      setPopupUser({ error: true })
    }
  }

  const formatCurrency = (value) => {
    const n = Number(value)
    if (!Number.isFinite(n)) return '-'
    try {
      return new Intl.NumberFormat('sk-SK', { style: 'currency', currency: 'EUR' }).format(n)
    } catch {
      return `${n.toFixed(2)} €`
    }
  }

  const parseReceiptTotal = (r) => {
    const raw =
      r.total_price ??
      r.totalPrice ??
      r.amount ??
      r.sum_amount ??
      r.total ??
      r.price ??
      r.suma ??
      r.amountWithoutVat ??
      0

    if (typeof raw === 'number') return raw
    if (typeof raw === 'string') {
      const cleaned = raw.replace(/[^\d,.-]/g, '').replace(',', '.')
      const val = parseFloat(cleaned)
      return Number.isFinite(val) ? val : 0
    }
    return 0
  }

  const parseUnits = (r) => {
    const raw = r.units ?? r.quantity ?? r.liters ?? 0
    if (typeof raw === 'number') return raw
    if (typeof raw === 'string') {
      const cleaned = raw.replace(/[^\d,.-]/g, '').replace(',', '.')
      const val = parseFloat(cleaned)
      return Number.isFinite(val) ? val : 0
    }
    return 0
  }

  const getLevelSets = (rootId) => {
    const l1 = new Set(childrenMap[rootId] || [])
    const l2 = new Set()
    const l3 = new Set()
    l1.forEach(id => (childrenMap[id] || []).forEach(c => l2.add(c)))
    l2.forEach(id => (childrenMap[id] || []).forEach(c => l3.add(c)))
    return { l1, l2, l3 }
  }

  const loadHoverData = async id => {
    try {
      const [usersRes, receiptsRes, cashbackRes] = await Promise.all([
        fetchWithAuth('/admin/users'),
        fetchWithAuth(`/users/${id}/receipts`),
        fetchWithAuth(`/cashback/${id}?month=${encodeURIComponent(ymNow())}`)
      ])

      let user = null
      if (usersRes.ok) {
        const users = await usersRes.json()
        user = users.find(u => String(u.id) === String(id)) || null
      }

      const userBucket = getUserFuelBucketStrict(user)

      let fuelSum = 0
      let fuelUnits = 0
      let fuelType = userBucket

      if (receiptsRes.ok) {
        const payload = await receiptsRes.json()
        const receipts = Array.isArray(payload) ? payload
          : Array.isArray(payload?.rows) ? payload.rows
          : Array.isArray(payload?.data) ? payload.data
          : []

        const monthReceipts = receipts.filter(r => {
          const d = getReceiptDate(r)
          return d ? isInCurrentMonth(d) : false
        })

        const processedReceipts = monthReceipts.filter(r => {
          const s = (r.status ?? r.receipt_status ?? r.receiptStatus ?? '').toLowerCase()
          return s === 'processed'
        })

        const filtered = userBucket
          ? processedReceipts.filter(r => getReceiptFuelBucket(r) === userBucket)
          : []

        fuelSum = filtered.reduce((sum, r) => sum + parseReceiptTotal(r), 0)
        fuelUnits = filtered.reduce((sum, r) => sum + parseUnits(r), 0)
      }

      let cb = null
      if (cashbackRes.ok) cb = await cashbackRes.json()

      const month = ymNow()
      let refEntries = []

      try {
        let resRef = await fetchWithAuth(
          `/wallet/${id}/entries?source=${encodeURIComponent('REFERRAL_BONUS')}&month=${encodeURIComponent(month)}`
        )
        if (!resRef.ok) resRef = await fetchWithAuth(`/wallet/${id}/entries`)
        if (resRef.ok) refEntries = await resRef.json()
      } catch {
        refEntries = []
      }

      const normalizeMonth = (row) => {
        if (row?.month) return String(row.month).slice(0, 7)
        if (row?.createdAt) {
          const d = new Date(row.createdAt)
          if (!Number.isNaN(d.getTime())) {
            const y = d.getFullYear()
            const m = String(d.getMonth() + 1).padStart(2, '0')
            return `${y}-${m}`
          }
        }
        return null
      }

      const filteredRef = Array.isArray(refEntries)
        ? refEntries.filter(e => {
            const src = String(e?.source || e?.type || '').toUpperCase()
            const mm = normalizeMonth(e)
            return src === 'REFERRAL_BONUS' && mm === month
          })
        : []

      const byChild = {}
      let refBonusTotal = 0

      filteredRef.forEach(e => {
        const amt = Number(e?.amount || 0)
        refBonusTotal += amt
        const childId = parseReferralSourceChildId(e?.source_id ?? e?.sourceId ?? e?.child_id)
        if (childId) byChild[childId] = (byChild[childId] || 0) + amt
      })

      const { l1, l2, l3 } = getLevelSets(id)
      const sumSet = (set) => Array.from(set).reduce((s, cid) => s + (byChild[cid] || 0), 0)
      const refL1 = sumSet(l1)
      const refL2 = sumSet(l2)
      const refL3 = sumSet(l3)

      setHoveredUser({
        meno: user?.meno ?? '-',
        priezvisko: user?.priezvisko ?? '-',
        email: user?.email ?? '-',
        fuelSum,
        fuelUnits,
        fuelType,
        membershipType: user?.membership?.membershipType?.type || '-',
        cbBase: Number(cb?.base ?? 0),
        cbL1: Number(cb?.l1 ?? 0),
        cbL2: Number(cb?.l2 ?? 0),
        cbL3: Number(cb?.l3 ?? 0),
        cbTotal: Number(cb?.total ?? 0),
        cbMonth: cb?.month || month,
        refBonusTotal,
        refL1,
        refL2,
        refL3,
        refMonth: month
      })
    } catch {
      setHoveredUser(null)
    }
  }

  return (
    <>
      <div style={{
        background: '#ededed',
        padding: 12,
        border: '1.5px solid #107c6b',
        borderRadius: 10,
        width: '100%',
        boxSizing: 'border-box',
        position: 'relative',
        height: '100%',
        minHeight: 0,
        overflow: 'auto'
      }}>
        <div style={{
          position: 'absolute',
          top: 16,
          right: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          zIndex: 10
        }}>
          <button
            onClick={() => setZoom(z => Math.min(z + 0.2, 3))}
            onMouseEnter={() => setHoverBtn('zoomIn')}
            onMouseLeave={() => {
              setHoverBtn(null)
              setPressedBtn(null)
            }}
            onMouseDown={() => setPressedBtn('zoomIn')}
            onMouseUp={() => setPressedBtn(null)}
            style={modalPrimaryButtonStyle(pressedBtn, 'zoomIn', hoverBtn === 'zoomIn')}
          >
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
              <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65" stroke="currentColor" strokeWidth="2"/>
              <line x1="11" y1="8" x2="11" y2="14" stroke="currentColor" strokeWidth="2"/>
              <line x1="8" y1="11" x2="14" y2="11" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </button>

          <button
            onClick={() => setZoom(z => Math.max(z - 0.2, 0.4))}
            onMouseEnter={() => setHoverBtn('zoomOut')}
            onMouseLeave={() => {
              setHoverBtn(null)
              setPressedBtn(null)
            }}
            onMouseDown={() => setPressedBtn('zoomOut')}
            onMouseUp={() => setPressedBtn(null)}
            style={modalPrimaryButtonStyle(pressedBtn, 'zoomOut', hoverBtn === 'zoomOut')}
          >
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
              <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65" stroke="currentColor" strokeWidth="2"/>
              <line x1="8" y1="11" x2="14" y2="11" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </button>

          <button
            onClick={() => {
              setZoom(1)
              setPan({ x: 0, y: 0 })
            }}
            onMouseEnter={() => setHoverBtn('zoomReset')}
            onMouseLeave={() => {
              setHoverBtn(null)
              setPressedBtn(null)
            }}
            onMouseDown={() => setPressedBtn('zoomReset')}
            onMouseUp={() => setPressedBtn(null)}
            style={modalPrimaryButtonStyle(pressedBtn, 'zoomReset', hoverBtn === 'zoomReset')}
          >
            <img src={resetIcon} alt="reset" width={24} height={24} style={{ pointerEvents: 'none' }} />
          </button>
        </div>

        <div style={{ marginBottom: 12 }}>
          {['red', 'gray', 'green'].map(color => (
            <div key={color} style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
              <span style={{
                display: 'inline-block',
                width: 32,
                height: 32,
                background: color,
                borderRadius: '50%',
                marginRight: 8
              }} />
              <span>{t(`LEGEND.${color.toUpperCase()}`)}</span>
            </div>
          ))}
        </div>

        <svg
          width={svgWidth}
          height={svgHeight}
          viewBox={`0 0 ${svgWidth / zoom} ${svgHeight / zoom}`}
          preserveAspectRatio="xMidYMin meet"
          onMouseDown={(e) => {
            setIsDragging(true)
            setDragStart({
              x: e.clientX - pan.x,
              y: e.clientY - pan.y
            })
          }}
          onMouseMove={(e) => {
            if (!isDragging) return
            setPan({
              x: e.clientX - dragStart.x,
              y: e.clientY - dragStart.y
            })
          }}
          onMouseUp={() => setIsDragging(false)}
          onMouseLeave={() => setIsDragging(false)}
          onWheel={(e) => {
            e.preventDefault()
            setZoom(z => {
              const delta = e.deltaY > 0 ? -0.1 : 0.1
              const next = z + delta
              if (next < 0.4) return 0.4
              if (next > 3) return 3
              return Number(next.toFixed(2))
            })
          }}
          style={{
            background: '#fafafa',
            border: '1px solid #ddd',
            borderRadius: 8,
            cursor: isDragging ? 'grabbing' : 'grab'
          }}
        >
          <g transform={`translate(${pan.x}, ${pan.y})`}>
            {links.map((l, i) => {
              const p = coords[l.source], c = coords[l.target]
              return (
                <line
                  key={i}
                  x1={p.x} y1={p.y}
                  x2={c.x} y2={c.y}
                  stroke="#107c6b"
                  strokeWidth={2}
                />
              )
            })}

            {nodes.map(n => {
              const pos = coords[n.id]
              const color = statuses[n.id] || 'red'
              const isHovered = hoveredNode === n.id
              const hasReferralBonus = (refBonusByChild[n.id] || 0) > 0

              return (
                <g
                  key={n.id}
                  transform={`translate(${pos.x},${pos.y})`}
                  onClick={() => loadPopupData(n.id)}
                  onMouseEnter={() => {
                    setHoveredNode(n.id)
                    loadHoverData(n.id)
                  }}
                  onMouseLeave={() => {
                    setHoveredNode(null)
                    setHoveredUser(null)
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <circle
                    r={16}
                    fill={color}
                    stroke={isHovered ? '#000' : '#fff'}
                    strokeWidth={isHovered ? 4 : 2}
                    style={{ transition: 'all 0.2s' }}
                  />

                  <text
                    textAnchor="middle"
                    dy="0.35em"
                    fill="#fff"
                    fontSize={12}
                    fontWeight="bold"
                    pointerEvents="none"
                  >
                    {n.id}
                  </text>

                  {hasReferralBonus && (
                    <text
                      x={17}
                      y={-15}
                      fontSize="14"
                      fontWeight="bold"
                      fill="#000"
                      textAnchor="start"
                      dominantBaseline="hanging"
                      style={{ pointerEvents: 'none' }}
                    >
                      B
                    </text>
                  )}
                </g>
              )
            })}
          </g>
        </svg>

        {hoveredNode && hoveredUser && (
          <div
            style={{
              position: 'absolute',
              top: coords[hoveredNode].y * zoom + pan.y + 170,
              left: coords[hoveredNode].x * zoom + pan.x - 100,
              background: '#fff',
              padding: '10px 12px',
              border: '1px solid #ccc',
              borderRadius: 6,
              pointerEvents: 'none',
              fontSize: 13,
              boxShadow: '0 0 6px rgba(0,0,0,0.15)',
              minWidth: 280
            }}
          >
            {hoveredUser.meno} {hoveredUser.priezvisko} <br /> {hoveredUser.email}
            <br />
            <strong>Suma účteniek (aktuálny mesiac):</strong> {formatCurrency(hoveredUser.fuelSum)}
            <br />
            <strong>Množstvo:</strong> {hoveredUser.fuelUnits}
            <br />
            <strong>Typ:</strong> {hoveredUser.fuelType || '-'}
            <br />
            <strong>Predplatné:</strong> {hoveredUser.membershipType}

            <div style={{ marginTop: 8, borderTop: '1px solid #eee', paddingTop: 8 }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>
                Cashback {hoveredUser.cbMonth}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '4px 6px' }}>Base</th>
                    <th style={{ textAlign: 'left', padding: '4px 6px' }}>L1</th>
                    <th style={{ textAlign: 'left', padding: '4px 6px' }}>L2</th>
                    <th style={{ textAlign: 'left', padding: '4px 6px' }}>L3</th>
                    <th style={{ textAlign: 'left', padding: '4px 6px' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ padding: '4px 6px' }}>{formatCurrency(hoveredUser.cbBase)}</td>
                    <td style={{ padding: '4px 6px' }}>{formatCurrency(hoveredUser.cbL1)}</td>
                    <td style={{ padding: '4px 6px' }}>{formatCurrency(hoveredUser.cbL2)}</td>
                    <td style={{ padding: '4px 6px' }}>{formatCurrency(hoveredUser.cbL3)}</td>
                    <td style={{ padding: '4px 6px', fontWeight: 600 }}>{formatCurrency(hoveredUser.cbTotal)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: 8, borderTop: '1px solid #eee', paddingTop: 8 }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>
                Referral bonus {hoveredUser.refMonth}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr>
                    <td style={{ padding: '4px 6px' }}>Bonus celkom</td>
                    <td style={{ padding: '4px 6px', fontWeight: 600 }}>
                      {formatCurrency(hoveredUser.refBonusTotal)}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: '4px 6px' }}>Bonus na L1</td>
                    <td style={{ padding: '4px 6px' }}>{formatCurrency(hoveredUser.refL1)}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '4px 6px' }}>Bonus na L2</td>
                    <td style={{ padding: '4px 6px' }}>{formatCurrency(hoveredUser.refL2)}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '4px 6px' }}>Bonus na L3</td>
                    <td style={{ padding: '4px 6px' }}>{formatCurrency(hoveredUser.refL3)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {showPopup && (
        <div
          onClick={() => setShowPopup(false)}
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 8,
              padding: 20,
              width: 400,
              maxHeight: '80%',
              overflowY: 'auto',
              position: 'relative'
            }}
          >
            <button
              onClick={() => setShowPopup(false)}
              style={{
                position: 'absolute', top: 10, right: 10,
                background: 'transparent', border: 'none',
                fontSize: 18, cursor: 'pointer'
              }}
            >
              ×
            </button>

            {popupUser === null && <p>{t('LOADING')}…</p>}
            {popupUser && popupUser.error && <p>{t('LOAD_ERROR')}</p>}
            {popupUser && !popupUser.error && (
              <>
                <h3>{popupUser.id}: {popupUser.meno} {popupUser.priezvisko}</h3>
                <div><strong>{t('EMAIL')}:</strong> {popupUser.email}</div>
                <div><strong>{t('ČLENSTVO ZAKÚPENÉ')}:</strong> {popupUser.aktivny ? t('YES') : t('NO')}</div>
                <div><strong>{t('TELEFÓN')}:</strong> {popupUser.telefon || '-'}</div>
                <div><strong>{t('REFERRAL_CODE')}:</strong> {popupUser.referral_code}</div>
                <div><strong>{t('PARENT_REFERRAL_CODE')}:</strong> {popupUser.parent_referral_code || '-'}</div>
                <div><strong>{t('REGISTRÁCIA')}:</strong> {popupUser.stav_uctu}</div>
                <div><strong>{t('CLENSTVO_ID')}:</strong> {popupUser.clenstvo_id || '-'}</div>
                <div><strong>{t('TYP_REGISTRÁCIE')}:</strong> {popupUser.membership?.membershipType?.type || '-'}</div>
                <div><strong>{t('PLATNOSŤ')}:</strong> {popupUser.membership ? `${popupUser.membership.valid_from.split('T')[0]} – ${popupUser.membership.valid_to.split('T')[0]}` : '-'}</div>
                <div><strong>{t('ULICA')}:</strong> {popupUser.ulica || '-'}</div>
                <div><strong>{t('MESTO')}:</strong> {popupUser.mesto || '-'}</div>
                <div><strong>{t('PSC')}:</strong> {popupUser.psc || '-'}</div>
                <div><strong>{t('KRAJINA')}:</strong> {popupUser.krajina || '-'}</div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  )
}
